import { InputAdornment } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useLayoutEffect, useState } from 'react';
import { useMutation } from '@apollo/client';

import { DELETE_MEDIA_LOGO, UPDATE_WORKSPACE_PROFILE } from '../../../../apis/workspace';
import { uploadWorkspaceProfileAvatar } from '../../../../utils/upload-avatar';
import { useAuth } from '../../../../context/AuthContext';
import Button from '../../../../components/atoms/button';
import Input from '../../../../components/atoms/input';
import Modal from '../../../../components/organisms/modal';
import PageName from '../../../../components/Heading/ProjectTitle';
import Toaster from '../../../../components/Snackbar/Toaster';
import UploadAvatarCard from '../../../../components/organisms/upload-avatar-card';
import useWorkspace from '../../../../context/WorkspaceContext';
import WorkspaceOptions from '../../../../components/molecules/workspace-options';
import useLoader from '../../../../context/LoaderContext';

const WorkspaceProfileSettings = () => {
  // init
  const { setIsLoading } = useLoader();
  const { authUser } = useAuth();
  const { workspaceProfile, setWorkspaceProfile, workspace } = useWorkspace();

  // states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', autoHideDuration: 3000 });
  const [isUploading, setIsUploading] = useState(false);
  const [workspaceProfileName, setWorkspaceProfileName] = useState('');
  const [workspaceProfileNameError, setWorkspaceProfileNameError] = useState('');

  // mutations/queries
  const [updateWorkspaceProfile, { loading: isWorkspaceProfileUpdating }] = useMutation(UPDATE_WORKSPACE_PROFILE);
  const [deleteMediaLogo, { loading: isAvatarDeleting }] = useMutation(DELETE_MEDIA_LOGO);

  const handleModalStatus = () => {
    setIsModalOpen((prevstate) => !prevstate);
  };

  const handleRemoveAvatar = async () => {
    if (workspaceProfile.logo) {
      try {
        const urlSplit = workspaceProfile.logo.split('/');
        const fileType = urlSplit[urlSplit.length - 1].split('.')[1];
        const variables = { type: 'user', fileType, userId: authUser?.id, workspaceId: workspace.id, workspaceProfileId: workspaceProfile.id };
        await deleteMediaLogo({ variables });
        setIsModalOpen(false);
        setToast((prevState) => ({ ...prevState, open: true, type: 'success', message: 'Avatar removed successfully' }));
        setWorkspaceProfile({ ...workspaceProfile, logo: null });
      } catch (error) {
        setToast((prevState) => ({ ...prevState, open: true, type: 'error', message: 'Something went wrong while deleting Avatar' }));
      }
    }
  };

  const handleUploadAvatar = async (avatar) => {
    try {
      await uploadWorkspaceProfileAvatar({ workspaceProfileId: workspaceProfile.id, userId: authUser?.id, workspaceProfileLogo: avatar, workspaceId: workspace.id });
      setWorkspaceProfile({ ...workspaceProfile, logo: avatar.dataURI });
      setToast((prevState) => ({ ...prevState, open: true, type: 'success', message: 'Avatar updated successfully' }));
    } catch (error) {
      setToast((prevState) => ({ ...prevState, open: true, type: 'error', message: 'Something went wrong while updating Avatar' }));
    }
  };

  // close the toast
  const handleLogoutSuccessClose = () => {
    setToast((prevstate) => ({ ...prevstate, open: false }));
  };

  const validateWorkspaceProfileName = () => {
    if (workspaceProfileName) {
      setWorkspaceProfileNameError('');
      return true;
    }
    setWorkspaceProfileNameError('Workspace Profile name is required');
    return false;
  };

  const handleWorkspaceNameChange = (event) => {
    if (workspaceProfile) setWorkspaceProfileNameError('');
    setWorkspaceProfileName(event.target.value);
  };

  const handleUpdateWorkspace = async () => {
    if (validateWorkspaceProfileName()) {
      try {
        const variables = {
          input: {
            id: workspaceProfile.id,
            displayName: workspaceProfileName,
          },
        };
        const { data: { updateWorkspaceProfile: newWorkspaceProfile } } = await updateWorkspaceProfile({ variables });
        setWorkspaceProfile(newWorkspaceProfile);
        setToast((prevState) => ({ ...prevState, open: true, type: 'success', message: 'Workspace Profile updated successfully' }));
      } catch (error) {
        setToast((prevState) => ({ ...prevState, open: true, type: 'error', message: 'Something went wrong while updating Workspace profile name' }));
      }
    }
  };

  useLayoutEffect(() => {
    if (workspaceProfile) {
      document.title = `${workspace.name} | Profile Settings`;
      setWorkspaceProfileName(workspaceProfile.displayName);
      setIsLoading(false);
    }
  }, [workspaceProfile]);

  return (
    <>
      <div className="page-header">
        <WorkspaceOptions />
        <PageName name="Profile Settings" />
      </div>
      <div className="settings-grid">
        <div className="grid-item">
          <div className="border-cards">
            <div className="border-card">
              <div className="border-card-input-container">
                <span className="border-card-label">Workspace Profile name</span>
                <Input error={!!workspaceProfileNameError} helperText={workspaceProfileNameError} value={workspaceProfileName} onChange={handleWorkspaceNameChange} autoFocus placeHolder="Enter full name" />
              </div>
              <div className="border-card-input-container">
                <span className="border-card-label">Email address</span>
                <Input
                  readOnly
                  value={authUser?.email || ''}
                  type="text"
                  className="input"
                  disabled
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <LockOutlined fontSize="small" style={{ color: 'black' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
              <Button isLoading={isWorkspaceProfileUpdating} disabled={workspaceProfile?.displayName === workspaceProfileName || isWorkspaceProfileUpdating} onClick={handleUpdateWorkspace} fullWidth={false} primary outlined small>Edit</Button>
            </div>
          </div>
        </div>
        <div className="grid-item">
          <UploadAvatarCard
            isUploading={isUploading}
            setIsUploading={setIsUploading}
            onRemove={handleModalStatus}
            onUpload={handleUploadAvatar}
            avatar={workspaceProfile?.logo}
            name={workspaceProfile?.displayName}
            title="Upload your Profile logo."
            uploadButtonText="Upload Photo"
            cancelButtonText="Remove"
          />
          <Modal
            title="Remove Avatar"
            body="Are you sure you want to remove profile avatar?"
            secondaryButton={(<Button isLoading={isAvatarDeleting} disabled={isAvatarDeleting} onClick={handleRemoveAvatar} danger>Remove Avatar</Button>)}
            primaryButton={(<Button disabled={isAvatarDeleting} primary outlined onClick={handleModalStatus}>Cancel</Button>)}
            isOpen={isModalOpen}
            actionAlignment="right"
            setIsOpen={setIsModalOpen}
          />
        </div>
      </div>
      <Toaster anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} {...toast} onClose={handleLogoutSuccessClose} />
    </>
  );
};

export default WorkspaceProfileSettings;
